import { Polyglot } from 'components/translate';
import { fabric } from 'fabric';
import Canvas from 'helpers/Canvas';
import Currency from 'helpers/Currency';
import 'helpers/fabric';
import { pipe } from 'helpers/Function';
import { parseFromStringXML, serializeToStringXML } from 'helpers/Markup';
import { isObject } from 'helpers/Object';
import { action, extendObservable, toJS } from 'mobx';
import { DatahubCertificateService } from 'modules/app/services/datahub/certificate';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import { CATEGORIES, LABELS } from 'modules/app/services/ga/constants';
import GtagService from 'modules/app/services/ga/GtagService';
import AuthService from 'modules/auth/AuthService';
import QrCodeHelper from 'modules/certificate/helpers/qrCodeHelper';
import {
  CERTIFICATE_EDITOR_HEIGHT,
  CERTIFICATE_EDITOR_WIDTH
} from 'modules/configManager/configCertificate/constants';
import {
  CERTIFICATE_LABEL_PREFIX,
  TEMPLATES
} from 'modules/configManager/constants';
import { FeatureToggles, FeatureToggleService } from 'modules/featureToggle';
import FileService from 'modules/file/FileService';
import MembershipService from 'modules/membership/MembershipService';
import { UiService } from 'modules/ui';
import queryString from 'query-string';
import { v4 } from 'uuid';

import './ConfigApi';
import Store from './ConfigStore';
import { streamingCondition as streamingType } from './configStreaming/constants';
import { CONFIG_TABS, FILE_ID_LENGTH, SETTINGS_ACTIVATE } from './constants';

import { CLUB_DRM_BETA_TESTER } from '../membership';
import { MembershipEventStore } from '../membershipEvent';
import { EVENTS } from '../membershipEvent/constants';

import Request from '@hotmart/request';

class ConfigService {
  errors = {
    '0001': { message: 'config.data.invalid_analytics_script_id' },
    '0027': { message: 'config.data.not_verified_domain', timeout: 2000 },
    '0026': { message: 'config.data.duplicate_domain' },
    '0028': { message: 'config.data.duplicate_subdomain' },
    '0031': { message: 'config.certificate.disabled' },
    '0034': { message: 'config.data.duplicate_subdomain' }
  };

  events = {
    sparkleCampaignEvents: GtagService.createEvents({
      common: {
        name: 'sparkle-campaign-settings',
        category: CATEGORIES.SPARKLE_CAMPAIGN,
        action: SETTINGS_ACTIVATE
      },
      accepted: {
        label: LABELS.ACCEPTED
      },
      refused: {
        label: LABELS.REFUSED
      }
    }),
    streamingCampaignEvents: GtagService.createEvents({
      common: {
        name: 'streaming-campaign-settings',
        category: CATEGORIES.STREAMING_CAMPAIGN,
        action: SETTINGS_ACTIVATE
      },
      accepted: {
        label: LABELS.ACCEPTED
      },
      refused: {
        label: LABELS.REFUSED
      }
    })
  };

  get certificateData() {
    return Store.certificate;
  }

  async fetchHelpData() {
    const data = await Request('config.getMembershipHelpContent');

    this.setHelpData(data);
  }

  async updateHelpData(data) {
    const cleanData = this.cleanEmptyFields(data);

    try {
      await Request('config.updateHelpContent', cleanData);
      UiService.setAlertSuccess('done');
    } catch (error) {
      UiService.handleError(error);
    }
  }

  setHelpData(data = {}) {
    extendObservable(Store.help, { ...data });
  }

  @action
  getHelpData() {
    return Store.help;
  }

  @action
  async fetchBasicData() {
    const data = await Request('config.getMembershipBasicData');

    this.setBasicData(data);
  }

  async existsBasicData(basicParameter) {
    try {
      return await Request('config.existsBasicData', { basicParameter });
    } catch {
      return false;
    }
  }

  @action
  setDeleteButtonPosition({ display, top, left }) {
    Store.deleteButtonCertificate = {
      display: display || Store.deleteButtonCertificate.display,
      top: top || Store.deleteButtonCertificate.top,
      left: left || Store.deleteButtonCertificate.left
    };
  }

  @action
  hideDeleteButton() {
    Store.deleteButtonCertificate = {
      display: 'none'
    };
  }

  cleanEmptyFields(data) {
    const keys = Object.keys(data);

    keys.forEach((key) => {
      if (data[key] === '') {
        data[key] = null;
      }
    });
    return data;
  }

  async updateBasicData(data) {
    const newData = this.cleanEmptyFields(data);

    this.setBasicData(newData);

    try {
      await Request('config.updateMembershipBasicData', { ...Store.data });
      UiService.setAlertSuccess('done');
    } catch (e) {
      this.requestErrorHandler(e);
      throw e;
    }
  }

  @action
  setBasicData(data = {}) {
    Object.assign(Store.data, data);
  }

  requestErrorHandler(e) {
    const { data } = e || {};

    if (data) {
      const { message, timeout } = this.errors[data.errorCode] || {};

      if (message) {
        UiService.setAlertError(message, timeout);
      }
    }
  }

  async checkDomainAvailability(domain) {
    try {
      await Request('config.testDomain', { domain });
      UiService.setAlertSuccess('config.data.verified_domain');
    } catch (e) {
      this.requestErrorHandler(e);
    }
  }

  setPrimaryColor(color) {
    Store.custom.lookAndFeel = color;
  }

  setSecondaryColor(color) {
    Store.custom.secondaryColor = color;
  }

  setCustomizationData(data = {}) {
    extendObservable(Store.custom, { ...data });
  }

  async fetchCustomizationData() {
    try {
      const data = await Request('config.findCustomizationDataBySubdomain');

      this.setCustomizationData({ selectedTemplate: data.template, ...data });

      return data;
    } catch (e) {
      this.requestErrorHandler(e);
    }
  }

  async initTemplate() {
    const availableTemplates = await this.fetchTemplates();
    const defaultTemplate = this.getDefaultTemplate(availableTemplates);

    this.setCustomizationData({
      availableTemplates,
      selectedTemplate: defaultTemplate
    });
  }

  getDefaultTemplate(templates) {
    return templates.find((item) => item.template === TEMPLATES.DEFAULT);
  }

  async fetchColors() {
    let data = null;

    try {
      data = await Request('config.getAllPossibleColors');
    } catch (e) {
      this.requestErrorHandler(e);
    }

    return data;
  }

  async fetchTemplates() {
    let data = null;

    try {
      data = await Request('config.getAllAvailableTemplates');
    } catch (e) {
      this.requestErrorHandler(e);
    }

    return data;
  }

  async getImage(id, entityType) {
    let response = null;

    if (id && entityType) {
      response = await FileService.fetchByType(id, entityType);
    }

    return response;
  }

  async updateCustomizationData(formData) {
    const { lookAndFeel, secondaryColor } = Store.custom;
    const data = {
      lookAndFeel,
      secondaryColor,
      ...formData
    };

    try {
      await Request('config.updateCustomizationDataBySubdomain', { ...data });
      UiService.setAlertSuccess('done');
    } catch (e) {
      this.requestErrorHandler(e);
    }
  }

  updateTemplate(template) {
    if (template && template.template) {
      MembershipService.template = template.template;
    }
  }

  setCertificateData(data = {}) {
    extendObservable(Store.certificate, { ...data });
  }

  formatCertificate(certificate) {
    return {
      ...certificate,
      value: certificate.id,
      label: certificate.name
    };
  }

  translateCertificatesTags = (certificates) =>
    certificates.forEach(this.translateTags);

  translateTags = ({ templateJson }) => {
    if (templateJson) {
      templateJson.objects.forEach((item) => {
        if (Canvas.isITextType(item) && Canvas.isTextMergedTag(item)) {
          item.mergedTag = item.text;
          const value = item.text.replace('*|', '').replace('|*', '');
          const translatedText = Polyglot.t(
            `${CERTIFICATE_LABEL_PREFIX}${value}`
          );
          const textWithoutPreFix = translatedText.replace(
            CERTIFICATE_LABEL_PREFIX,
            ''
          );

          item.text = `*|${textWithoutPreFix}|*`;
        }
      });
    }
  };

  translateDefaultCertificate = (certificate) => {
    const { templateJson } = certificate;

    if (!templateJson) {
      return certificate;
    }

    const { objects, backgroundImage } = templateJson;

    const translatedObjects =
      objects &&
      objects.map((object) => {
        if (Canvas.isITextType(object) && !Canvas.isTextMergedTag(object)) {
          return {
            ...object,
            text: Polyglot.t(`${CERTIFICATE_LABEL_PREFIX}${object.text}`),
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            left: (CERTIFICATE_EDITOR_WIDTH - object.width) / 2
          };
        }

        return object;
      });

    const translatedBackgroundImage = {
      ...backgroundImage,
      width: CERTIFICATE_EDITOR_WIDTH,
      height: CERTIFICATE_EDITOR_HEIGHT
    };

    return {
      ...certificate,
      templateJson: {
        ...templateJson,
        objects: translatedObjects,
        backgroundImage: translatedBackgroundImage
      }
    };
  };

  translateCertificates = (certificates) =>
    certificates.forEach((item) => {
      !item.id && this.translateDefaultCertificate(item.templateJson);
    });

  getImageLink(certificate) {
    const { templateJson: data = {} } = certificate;

    if (data && data.objects) {
      data.objects.forEach((obj) => {
        if (Canvas.isImage(obj) && obj.src && !QrCodeHelper.isQRCode(obj.src)) {
          obj.src = Canvas.formatCanvasImageURL(obj.src);
        }
      });
    }

    if (data && data.backgroundImage) {
      data.backgroundImage.src = Canvas.formatCanvasImageURL(
        data.backgroundImage.src
      );
    }

    return certificate;
  }

  certficatesGetImagesLinks = (certificates) =>
    certificates.forEach(this.getImageLink.bind(this));

  async fetchCertificateData() {
    const data = await Request('config.getMembershipCertificateSettings');

    if (!data.certificates) {
      this.setCertificateData(data);
      return;
    }

    const formatedCertificates = data.certificates.map((certificate) => {
      return pipe(
        this.formatObjects,
        this.getImageLink,
        this.formatCertificate,
        QrCodeHelper.replaceCertificateTagWithQRCode
      )(certificate);
    });

    this.setCertificateData({
      ...data,
      certificates: formatedCertificates
    });
  }

  async fetchDefaultCertificate() {
    const data = await Request('config.getDefaultCertificateTemplate');

    return pipe(this.translateDefaultCertificate, this.formatObjects)(data);
  }

  formatObjects = (certificate) => {
    const { templateJson } = certificate || {};
    const { objects } = templateJson || [];

    return {
      ...certificate,
      templateJson: {
        ...templateJson,
        objects: this.formatMergeTags(objects)
      }
    };
  };

  formatMergeTags = (objects) => {
    if (!objects) {
      return [];
    }

    const formatedObjects = objects.map((object) => {
      return pipe(this.translateTags, this.blockEditOnMergeTags)(object);
    });

    return formatedObjects;
  };

  // eslint-disable-next-line no-dupe-class-members
  translateTags = (object) => {
    if (Canvas.isITextType(object) && Canvas.isTextMergedTag(object)) {
      const regex = /(\*\||\|\*)/gi;
      const value = object.text.replace(regex, '');
      const translatedText = Polyglot.t(`${CERTIFICATE_LABEL_PREFIX}${value}`);
      const textWithoutPreFix = translatedText.replace(
        CERTIFICATE_LABEL_PREFIX,
        ''
      );

      return {
        ...object,
        mergedTag: object.text,
        text: `*|${textWithoutPreFix}|*`
      };
    }

    return object;
  };

  blockEditOnMergeTags = (object) => {
    if (Canvas.isITextType(object) && Canvas.isTextMergedTag(object)) {
      return { ...object, editable: false };
    }

    return object;
  };

  fileTag(id) {
    return `*|${id}|*`;
  }

  getImageID({ src = '' }) {
    const { url } = queryString.parseUrl(src);
    const parts = url.split('/');
    const lastIndex = parts.length - 1;

    return parts[lastIndex];
  }

  isValidFileID(id) {
    if (!id) {
      return false;
    }

    const validLength = this.isOldFileID(id) || this.isNewFileID(id);
    const hasExtesion = id.indexOf('.') >= 0;

    return validLength && !hasExtesion;
  }

  isOldFileID = (id) => id && id.length === FILE_ID_LENGTH.OLD;

  isNewFileID = (id) => id && id.length === FILE_ID_LENGTH.NEW;

  replaceBackgroundURL(certificate) {
    const { templateJson: data, images } = certificate || {};

    if (data && data.backgroundImage) {
      const imageId = this.getImageID(data.backgroundImage);

      if (this.isValidFileID(imageId)) {
        if (this.isOldFileID(imageId)) {
          data.backgroundImage.src = this.fileTag(imageId);
        }

        images.background = imageId;
      }
    }
  }

  replaceInnerImagesURL(certificate) {
    const { templateJson: data, images } = certificate || {};

    if (data && data.objects) {
      data.objects.forEach((obj) => {
        if (Canvas.isImage(obj) && obj.src) {
          const imageId = this.getImageID(obj);

          if (this.isValidFileID(imageId)) {
            if (this.isOldFileID(imageId)) {
              obj.src = this.fileTag(imageId);
            }
            if (!images.innerImages) {
              images.innerImages = [];
            }
            images.innerImages.push(imageId);
          }
        }
      });
    }
  }

  replaceImgReferenceWithId(img) {
    const fileID = this.getImageID({
      src: img.href.baseVal || img.href.animVal || img.href
    });

    if (this.isOldFileID(fileID) && this.isValidFileID(fileID)) {
      if (isObject(img.href)) {
        img.href.baseVal = this.fileTag(fileID);
      } else {
        img.href = this.fileTag(fileID);
      }
    }
  }

  replaceImagesInSVG(data = {}) {
    const oDOM = parseFromStringXML(data);
    const images = oDOM.querySelectorAll('image');

    images.forEach((img) => this.replaceImgReferenceWithId(img));

    return serializeToStringXML(oDOM);
  }

  replaceURLsWithFileID(certificates = []) {
    certificates.forEach((certificate) => {
      certificate.images = {};
      this.replaceBackgroundURL(certificate);
      this.replaceInnerImagesURL(certificate);
      certificate.templateSvg = this.replaceImagesInSVG(
        certificate.templateSvg
      );
    });
  }

  getCanvases(certificates) {
    return certificates.map((certificate) => {
      const { templateJson } = certificate;

      return this.createCanvas(templateJson);
    });
  }

  async formatCertificateToSend(certificates) {
    if (certificates) {
      const canvases = await Promise.all(this.getCanvases(certificates));

      return certificates.map((item, idx) => {
        const { enableStudentCertificate, id, images, name } = item;
        const currentCanvas = canvases[idx];

        const { templateSvg, templateJson } =
          this.getCertificateSVG(currentCanvas);

        const certificate = {
          enableStudentCertificate,
          images,
          templateJson,
          templateSvg
        };

        if (id) {
          certificate.id = id;
        }

        if (name) {
          certificate.name = name;
        }

        return certificate;
      });
    }
  }

  removeEmptyCertificates(certificates) {
    return certificates.filter(
      (certificate) => certificate.templateJson !== null
    );
  }

  async updateCertificateData() {
    const { allowProgressDisplay } = Store.certificate;
    let { certificates } = Store.certificate;

    try {
      certificates = certificates.filter(
        (certificate) => certificate.templateJson !== null
      );
      let formatedCertificates = toJS(certificates);

      formatedCertificates = await this.formatCertificateToSend(
        formatedCertificates
      );
      this.replaceURLsWithFileID(formatedCertificates);

      let hasQRCode = false;

      formatedCertificates = formatedCertificates.map((certificate) => {
        const response =
          QrCodeHelper.replaceCertificateQrCodeWithTag(certificate);

        if (response.hasQRCode) {
          hasQRCode = true;
        }

        return response.certificate;
      });

      const data = {
        certificates: formatedCertificates,
        allowProgressDisplay
      };

      if (hasQRCode) {
        DatahubCertificateService.sendSaveWithQRCodeEvent();
      }

      await Request('config.updateMembershipCertificateSettings', data);
      UiService.setAlertSuccess('done');
    } catch (e) {
      this.requestErrorHandler(e);
    }
  }

  createCanvas(data) {
    const canvas = new fabric.Canvas(null, {
      width: CERTIFICATE_EDITOR_WIDTH,
      height: CERTIFICATE_EDITOR_HEIGHT
    });

    return Canvas.loadCanvasData(canvas, data);
  }

  getCertificateSVG(canvas) {
    const objs = canvas.getObjects();
    const formattedCanvas = Canvas.getTranslatedCertificate(canvas, objs);

    return formattedCanvas;
  }

  getCertificatePreviewLink(canvas) {
    const certificate = this.getCertificateSVG(canvas);
    const { xml: templateSvg } = QrCodeHelper.replaceXMLQrCodeWithTag(
      certificate.templateSvg
    );

    return Request('config.getCertificateLinkPreview', { templateSvg });
  }

  async createCertificate(label) {
    const defaultCertificate = await this.fetchDefaultCertificate();
    const certificate = {
      enableStudentCertificate: false,
      label,
      name: label,
      value: v4(),
      ...defaultCertificate
    };

    return certificate;
  }

  getAskStatus(productId) {
    return Request('ask.verify', { productId });
  }

  changeAskStatus(productId, status) {
    return Request('ask.changeAskStatus', { productId, status });
  }

  getStreamingAndCommunicationStatus() {
    return Request('config.findStreamingStatusBySubdomain');
  }

  getStreamingStatus() {
    return Request('config.findStreamingStatus');
  }

  getCommunicationStatus() {
    return Request('config.findSparkleCampaignStatusBySubdomain');
  }

  async updateStreamingAndSparkleData(data) {
    try {
      await Request(
        'config.updateStreamingAndSparkleCommunicationStatus',
        data
      );

      this.processStreamingStatusChanged(data.active);
      this.processHotmartCommunicationStatusChanged(data.communicationActive);
      UiService.setAlertSuccess('done');
    } catch (error) {
      UiService.handleError(error);
    }
  }

  async updateSparkleData(status) {
    try {
      await Request('config.updateSparkleCampaignStatus', { active: status });
      this.processHotmartCommunicationStatusChanged(status);
      UiService.setAlertSuccess('done');
    } catch (error) {
      UiService.handleError(error);
      throw error;
    }
  }

  processStreamingStatusChanged(streamingActive) {
    if (streamingActive) {
      MembershipService.enableStreaming();
      GtagService.send(this.events.streamingCampaignEvents.accepted);
      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          label: EVENTS_LABELS.STREAMING_CAMPAIGN.ACTIVATE_HOTMART_VIDEO_PLAYER,
          action: EVENTS_INTERACTIONS.TOGGLE_ON,
          category: EVENTS_CATEGORY.STREAMING_CAMPAIGN.NAME
        })
      });
    } else {
      MembershipService.disableStreaming();

      if (!MembershipService.isOnlineEventTemplate) {
        const { defaultTemplate } = Store.custom;

        this.setCustomizationData({ selectedTemplate: defaultTemplate });
        this.updateTemplate(defaultTemplate);
      }

      GtagService.send(this.events.streamingCampaignEvents.refused);

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          label: EVENTS_LABELS.STREAMING_CAMPAIGN.ACTIVATE_HOTMART_VIDEO_PLAYER,
          action: EVENTS_INTERACTIONS.TOGGLE_OFF,
          category: EVENTS_CATEGORY.STREAMING_CAMPAIGN.NAME
        })
      });
    }
  }

  processHotmartCommunicationStatusChanged(communicationActive) {
    if (communicationActive) {
      MembershipService.enableSparkleCommunication();
      GtagService.send(this.events.sparkleCampaignEvents.accepted);

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          label: EVENTS_LABELS.SPARKLE_CAMPAIGN.ACTIVATE_HOTMART_COMMUNICATION,
          action: EVENTS_INTERACTIONS.TOGGLE_ON,
          category: EVENTS_CATEGORY.SPARKLE_CAMPAIGN.NAME
        })
      });
    } else {
      MembershipService.disableSparkleCommunication();
      GtagService.send(this.events.sparkleCampaignEvents.refused);

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          label: EVENTS_LABELS.SPARKLE_CAMPAIGN.ACTIVATE_HOTMART_COMMUNICATION,
          action: EVENTS_INTERACTIONS.TOGGLE_OFF,
          category: EVENTS_CATEGORY.SPARKLE_CAMPAIGN.NAME
        })
      });
    }
  }

  async updateStreamingStatus(status) {
    try {
      await Request('config.updateStreamingStatus', status);

      this.processStreamingStatusChanged(status.active);
    } catch (error) {
      UiService.handleError(error);
      throw error;
    }
  }

  async updateDrmStatus(active) {
    try {
      await Request('config.updateDrmStatus', { active });
      MembershipService.setStreamingDrmProtection(active);
      return active;
    } catch (error) {
      UiService.handleError(error);
      throw error;
    }
  }

  /** @returns {Promise<{addOnCost?: string, promotionalCost?: string} | undefined>} */
  async getStreamingCosts() {
    const { defaultCost, promotionalCost, currency } = await Request(
      'config.findStreamingCostsBySubdomain'
    );
    const formatedAddonCosts = {};

    if (currency) {
      if (defaultCost) {
        formatedAddonCosts.addOnCost = Currency.formatCurrencySymbol(
          defaultCost,
          currency
        );
      }

      if (promotionalCost) {
        formatedAddonCosts.promotionalCost = Currency.formatCurrencySymbol(
          promotionalCost,
          currency
        );
      }
    }

    return formatedAddonCosts;
  }

  resolveStreamingCondition() {
    const isStreamingEnabled = MembershipService.isStreamingEnabled;
    const isStreamingCampaign = MembershipService.isStreamingCampaignCandidate;

    if (!isStreamingEnabled && isStreamingCampaign) {
      return streamingType.STREAMING_CAMPAIGN_ELIGIBLE;
    } else if (isStreamingEnabled && isStreamingCampaign) {
      return streamingType.STREAMING_CAMPAIGN_ACTIVATED;
    }

    return streamingType.DEFAULT;
  }

  async getStreamingCostConsideringStreamingCampaign() {
    const isStreamingCampaign = MembershipService.isStreamingCampaignCandidate;
    const costs = await this.getStreamingCosts();

    if (costs.promotionalCost && isStreamingCampaign) {
      return costs.promotionalCost;
    }

    return costs.addOnCost;
  }

  async getBundleStreamingCosts() {
    try {
      const { currency, value } = await Request(
        'config.findBundleStreamingCost'
      );

      if (!(currency && value)) {
        return '';
      }

      return Currency.formatCurrencySymbol(value, currency);
    } catch (error) {
      return '';
    }
  }

  get showDrmConfig() {
    const featureToggle = FeatureToggleService.isEnabled(
      FeatureToggles.ConfigDrm
    );

    return featureToggle && AuthService.hasAuthorities(CLUB_DRM_BETA_TESTER);
  }

  get tabHighlightConfig() {
    return [
      {
        tabIndex: CONFIG_TABS.STREAMING,
        eventName: EVENTS.DRM_SETTINGS,
        hidden: !this.showDrmConfig
      },
      {
        tabIndex: CONFIG_TABS.SPARKLE,
        eventName: EVENTS.SPARKLE_CAMPAIGN_SETTINGS
      }
    ];
  }

  get highlightTabs() {
    return this.tabHighlightConfig
      .filter(
        ({ eventName, hidden }) =>
          !hidden && MembershipEventStore.hasCampaignEvent(eventName)
      )
      .map(({ tabIndex }) => tabIndex);
  }
}

export default new ConfigService();
